import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../AppContext";
import AuthService from "../services/auth.service";
import useAxiosFetch from "../common/useAxiosFetch";
import useDismiss from "./useDismiss";
import { updateDb } from "../services/updateDb";
import { getInitials } from "./routeMeta";

const AccountMenu = ({ collapsed }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, setMobileNavOpen } = useAppContext();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ bottom: 0, left: 0 });
  const [pictureUrl, setPictureUrl] = useState(null);
  const cardRef = useRef(null);
  const popRef = useRef(null);
  const dismissRefs = useMemo(() => [cardRef, popRef], []);

  const { data: pictureData } = useAxiosFetch(currentUser?.ssoEnabled ? "/api/users/profile-picture" : null);
  useEffect(() => {
    if (pictureData?.profilePictureUrl) setPictureUrl(pictureData.profilePictureUrl);
  }, [pictureData]);

  const close = useCallback(() => setIsOpen(false), []);
  useDismiss(isOpen, dismissRefs, close);

  useLayoutEffect(() => {
    if (!isOpen || !cardRef.current) return undefined;
    const place = () => {
      const rect = cardRef.current.getBoundingClientRect();
      const nav = cardRef.current.closest("nav");
      const navRight = nav ? nav.getBoundingClientRect().right : rect.right;
      setPosition({ bottom: window.innerHeight - rect.bottom, left: navRight + 12 });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [isOpen, collapsed]);

  const isAdmin = currentUser.role === "superadmin" || currentUser.role === "admin";
  const roleLabel = t(`shell.role_${currentUser.role}`, currentUser.role);

  const goTo = (path) => {
    close();
    setMobileNavOpen(false);
    navigate(path);
  };

  const changeLanguage = (language) => {
    localStorage.setItem("language", language);
    i18n.changeLanguage(language);
    // Remember it on the account too, so mails follow the chosen language
    updateDb("/api/users/language", { language }).catch(() => {});
  };

  const avatar = (
    <span className="account-avatar" style={pictureUrl ? { backgroundImage: `url(${pictureUrl})` } : undefined}>
      {!pictureUrl && getInitials(currentUser.name)}
    </span>
  );

  return (
    <>
      <button
        type="button"
        ref={cardRef}
        className={`account-card ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        title={collapsed ? currentUser.name : undefined}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {avatar}
        <span className="account-card-text">
          <span className="account-card-name">{currentUser.name}</span>
          <span className="account-card-role">{roleLabel}</span>
        </span>
        <i className="icon-sort account-card-chevron" />
      </button>

      {isOpen && createPortal(
        <div ref={popRef} className="account-pop" style={{ bottom: position.bottom, left: position.left }} role="menu">
          <div className="account-pop-head">
            {avatar}
            <div>
              <div className="account-pop-name">{currentUser.name}</div>
              <div className="account-pop-email">{currentUser.email}</div>
            </div>
          </div>

          <button type="button" className="account-pop-item" onClick={() => goTo("/edituser?userId=" + currentUser.user_id)}>
            <i className="icon-Gebruiker" /> {t("my_profile")}
          </button>
          {isAdmin && currentUser.planId !== "free" && (
            <button type="button" className="account-pop-item" onClick={() => goTo("/subscriptions")}>
              <i className="icon-subscriptions" /> {t("subscriptions")}
            </button>
          )}

          <div className="account-pop-item static">
            <i className="icon-globe" /> {t("language")}
            <span className="lang-toggle" role="group" aria-label={t("language")}>
              {["nl", "en"].map((lang) => (
                <button
                  key={lang}
                  type="button"
                  className={i18n.language === lang ? "active" : ""}
                  onClick={() => changeLanguage(lang)}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </span>
          </div>

          <div className="account-pop-divider" />
          <button type="button" className="account-pop-item logout" onClick={() => AuthService.logout()}>
            <i className="icon-Log-out" /> {t("logout")}
          </button>
        </div>,
        document.body
      )}
    </>
  );
};

export default AccountMenu;
